import {
  ILibrarySectionMatch,
  ShowInfoPushAction,
} from "api/showInfoLibrary/types";
import {
  INTERNAL_NAME_MAX_LENGTH,
  INTERNAL_NAME_PATTERN,
} from "view/apps/Organiser/pages/Libraries/ShowInfo/constants";

type Draft = {
  action: ShowInfoPushAction;
  masterTermId: string;
  title: string;
  internalName: string;
  matches: ILibrarySectionMatch[];
};

/** Whether the pushed title still runs into a library section of that name. */
export const collides = (
  title: string,
  matches: ILibrarySectionMatch[]
): boolean =>
  matches.some(
    (match) => match.title.trim().toLowerCase() === title.trim().toLowerCase()
  );

/**
 * What is wrong with the typed internal name, or `null`. An empty value is not
 * an issue here — whether it is *required* is `canSubmitPush`'s call.
 */
export const internalNameIssue = (
  internalName: string,
  matches: ILibrarySectionMatch[]
): string | null => {
  const trimmed = internalName.trim();

  if (!trimmed) return null;

  if (trimmed.length > INTERNAL_NAME_MAX_LENGTH) {
    return `Up to ${INTERNAL_NAME_MAX_LENGTH} characters.`;
  }

  if (!INTERNAL_NAME_PATTERN.test(trimmed)) {
    return "Only letters, numbers and / \\ _ - are allowed.";
  }

  const taken = matches.some(
    (match) =>
      (match.internalName ?? "").trim().toLowerCase() === trimmed.toLowerCase()
  );

  return taken ? "That internal name is already used for this title." : null;
};

/**
 * Whether `Duplicate warning` can be confirmed as it stands.
 *
 * `Replace` needs a target. `Create new section` needs a title, and then either
 * a title the library does not hold or an internal name to tell this one
 * apart — the two ways out its helper text offers, and the server's own rule.
 */
const canSubmitPush = ({
  action,
  masterTermId,
  title,
  internalName,
  matches,
}: Draft): boolean => {
  if (action === ShowInfoPushAction.REPLACE) return !!masterTermId;

  const nextTitle = title.trim();

  if (!nextTitle) return false;
  if (internalNameIssue(internalName, matches)) return false;

  return !collides(nextTitle, matches) || !!internalName.trim();
};

export default canSubmitPush;
